import { formatTarget, type ClientTarget } from '@features/clients';
import { ApiError, UnauthenticatedError } from '@lib/api';
import { useQuery } from '@tanstack/react-query';

import { getActivationMuseum, listActivationMuseums } from '../api/activation-tracking';

import type { ActivationDetail, ActivationMuseum } from '../schemas/activation';

/**
 * Server state for the Feature Activation screens.
 *
 * The target is in every key, for the reason `use-clients.ts` gives: switching
 * environment must refetch rather than show the previous one's museums under
 * the new heading, and two environments never share a cache entry.
 */

export const activationKeys = {
    all: ['activation-tracking'] as const,
    list: (target: string) => [...activationKeys.all, 'list', target] as const,
    detail: (target: string, tenantId: string) =>
        [...activationKeys.all, 'detail', target, tenantId] as const,
};

/** Same policy as the clients and flags screens: a 4xx is an answer, not a blip. */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useActivationMuseums(target: ClientTarget) {
    const id = formatTarget(target);

    return useQuery<ActivationMuseum[]>({
        // Disabled, not guessed, for an ephemeral target with no slot — the
        // screen explains that state instead of querying it.
        queryKey: activationKeys.list(id ?? 'unaddressable'),
        queryFn: ({ signal }) => listActivationMuseums(target, signal),
        enabled: id !== null,
        retry: retryUnlessClientError,
    });
}

export function useActivationMuseum(target: ClientTarget, tenantId: string) {
    const id = formatTarget(target);

    return useQuery<ActivationDetail>({
        queryKey: activationKeys.detail(id ?? 'unaddressable', tenantId),
        queryFn: ({ signal }) => getActivationMuseum(target, tenantId, signal),
        enabled: id !== null && tenantId.length > 0,
        retry: retryUnlessClientError,
    });
}
